<template>
  <v-dialog v-model="localDialog" persistent :max-width="isPaymentActive ? 400 : 600" @keydown.esc="closeDialog">

    <template v-slot:activator="{ on, attrs }">
      <v-btn depressed v-bind="attrs" v-on="on" :class="activatorClass" :color="activatorColor"
        :loading="activatorLoadingValue" :block="!activatorFab" :fab="activatorFab"
        :width="activatorWidth" :height="activatorHeight" :disabled="activatorDisabled">
        <v-icon v-if="activatorIcon" :left="!activatorFab" :size="activatorIconSize">{{ activatorIcon }}</v-icon>
        <template v-if="activatorText">{{ activatorTranslate ? $t(activatorText) : activatorText }}</template>
      </v-btn>
    </template>

    <v-card relative>
      <v-btn absolute class="mt-2" v-if="!isPaymentActive" :style="$vuetify.rtl == true ? 'left: 0' : 'right: 0'" small
        fab color="grey lighten-1" text @click="closeDialog">
        <v-icon color="grey lighten-1">mdi-close</v-icon>
      </v-btn>

      <div class="d-flex px-2 align-center justify-space-around" v-if="isPaymentActive">
        <v-col cols="auto">
          <v-row align="center">
            <v-col cols="auto" class="pa-0">
              <div class="d-block pr-3 pt-3 grey lighten-1 rounded-pill"></div>
            </v-col>
            <v-col cols="auto">
              <h6 class="my-0 py-0">
                {{ $t("the amount required") }} :
                <strong>{{ totalRequired | float }}</strong>
              </h6>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="auto" class="pa-0">
          <v-row>
            <v-col cols="auto">
              <div class="d-block pr-3 pt-3 grey lighten-1 rounded-pill"></div>
            </v-col>
            <v-col cols="auto">
              <h6 class="my-0 py-0" :style="remainingAmount > 0 ? 'color: #d50000' : 'color: #0bac30'">
                <span v-if="remainingAmount > 0">{{ $t("remaining") }} :</span>
                <span v-else-if="remainingAmount < 0">{{ $t("more") }} :</span>
                <span v-else>{{ $t("ok") }} :</span>
                <strong>{{ remainingAmount | float }}</strong>
              </h6>
            </v-col>
          </v-row>
        </v-col>
      </div>

      <v-col cols="12" class="pa-10" v-if="!isPaymentActive">
        <v-row align-items="center">

          <v-col cols="4" v-for="method in paymentMethods" :key="method.id">
            <v-card outlined height="150" class="ma-auto rounded-xl d-flex justify-center align-center mt-2"
              @click="startPayment([method])" width="150">
              <div>
                <img :src="$api.serverUrl + method.image" height="50" class="d-block ma-auto" :alt="method.name"
                  @error="defaultPaymentImg">
                <div class="subtitle-1 text-center mt-2">{{ method.name }}</div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="4">
            <v-card outlined height="150" class="ma-auto rounded-xl d-flex justify-center align-center mt-2"
              @click="startSplitPayment()" width="150">
              <div>
                <img src="@/assets/img/pos_svg/cash_credit.svg" height="50" alt="split" class="d-block ma-auto"
                  @error="defaultPaymentImg">
                <div class="subtitle-1 text-center mt-2">{{ $t('cash + network') }}</div>
              </div>
            </v-card>
          </v-col>

        </v-row>
      </v-col>

      <v-form class="px-5 pb-5" onsubmit="return false;" v-if="isPaymentActive">
        <v-col cols="12" sm="12">

          <v-col cols="12" class="py-0 mb-2" v-if="currentStepInfo.id === 2">
            <v-row>
              <v-col cols="6" class="py-1" v-for="card in cardOptions" :key="card.id">
                <v-btn depressed @click="setCartType(card.id)" text block class="mx-2"
                  :class="cart_type == card.id ? 'gray11' : ''" color="primary">
                  <v-icon left color="success" v-if="cart_type == card.id">mdi-check</v-icon>
                  <v-icon left color="success" v-else></v-icon>
                  <img :src="card.icon" height="20" :alt="card.name">
                </v-btn>
              </v-col>
            </v-row>
          </v-col>

          <h5 class="mb-5">{{ currentStepInfo.name }}</h5>

          <NumberPad :value="currentAmount" :backValueMethod="updateAmount" :number="true"></NumberPad>

        </v-col>

        <v-row justify="space-between" class="mb-1">

          <v-col cols="auto" class="py-0">
            <v-btn color="error" :height="40" text plain @click="closeDialog">{{ $t("cancel") }}</v-btn>
          </v-col>

          <v-col cols="auto" class="py-0" v-if="currentStepIndex > 0">
            <v-btn color="error" :height="40" min-width="100" depressed @click="prevStep">{{ $t("back") }}</v-btn>
          </v-col>

          <v-col cols="auto" class="px-5 py-0" v-if="hasNextStep">
            <v-btn color="success" :height="40" min-width="120" @click="nextStep" class="shadow">
              {{ $t("next") }}
            </v-btn>
          </v-col>

          <v-col cols="auto" class="px-5 py-0" v-if="!hasNextStep">
            <v-btn color="success" :height="40" min-width="120" :disabled="!isValid" @click="submitPayment" depressed>
              {{ $t("pay") }}
            </v-btn>
          </v-col>

        </v-row>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import NumberPad from "../NumberPad.vue";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("InvoiceStore");

export default {
  name: "PaymentsModal",
  components: { NumberPad },
  props: {
    paymentMethods: { type: Array, default: () => [] },
    machineID: { default: "" },
    // When provided, overrides InvoiceStore value
    totalRequiredOverride: { type: [Number, String], default: undefined },
    backValueMethod: { type: Function, required: true },
    // Activator button props
    activatorText: { type: String, default: 'paid' },
    activatorTranslate: { type: Boolean, default: true },
    activatorColor: { type: String, default: 'gray4' },
    activatorClass: { type: String, default: 'white--text' },
    activatorIcon: { type: String, default: 'mdi-cash-plus' },
    activatorIconSize: { type: [Number, String], default: 25 },
    activatorLoading: { type: Boolean, default: undefined },
    activatorFab: { type: Boolean, default: false },
    activatorWidth: { type: [Number, String], default: undefined },
    activatorHeight: { type: [Number, String], default: undefined },
    sales_type_id: { type: [Number, String], default: null },
    activatorDisabled: { type: Boolean, default: false },
  },
  watch: {
    localDialog() {
      switch (+this.sales_type_id) {
        case 1:
          this.startPayment([]); // not paid
          break;
        case 2:
          this.startPayment([{ id: 1, name: this.$t('cash') }]); // cash
          break;
        case 3:
          this.startPayment([{ id: 2, name: this.$t('network') }]); // network
          break;
        case 4:
          this.startSplitPayment([{ id: 1, name: this.$t('cash') }, { id: 2, name: this.$t('network') }]); // cash + network
          break;
        case 5:
          this.startPayment([{ id: 3, name: this.$t('tabby') }]); // tabby
          break;
        case 6:
          this.startPayment([{ id: 4, name: this.$t('tamara') }]); // tamara
          break;

        default:
          this.startPayment([]);
          break;
      }
    },
  },
  data() {
    return {
      localDialog: false,

      // Logic State
      stepsQueue: [],
      currentStepIndex: 0,
      paymentsMap: {},

      // Card Type State
      cart_type: 3,
      cardOptions: [
        { id: 3, name: 'mada', icon: require('@/assets/img/svg/mada.svg') },
        { id: 1, name: 'visa', icon: require('@/assets/img/svg/visa.svg') },
        { id: 2, name: 'mastercard', icon: require('@/assets/img/svg/mastercard.svg') },
        { id: 4, name: 'khaleg', icon: require('@/assets/img/svg/khaleg_network.png') },
      ]
    };
  },
  computed: {
    ...mapGetters(["getInvoiceStore"]),

    activatorLoadingValue() {
      return this.activatorLoading !== undefined ? this.activatorLoading : this.pageData?.isLoading;
    },

    isPaymentActive() {
      return this.stepsQueue.length > 0;
    },

    currentStepInfo() {
      return this.stepsQueue[this.currentStepIndex] || {};
    },

    currentAmount() {
      return this.paymentsMap[this.currentStepInfo.id] || "";
    },

    totalRequired() {
      if (this.totalRequiredOverride !== undefined && this.totalRequiredOverride !== null && this.totalRequiredOverride !== '') {
        return Number(this.totalRequiredOverride) || 0;
      }
      return this.getInvoiceStore.total_price_after_discount_with_vat || 0;
    },

    totalPaidSoFar() {
      return Object.values(this.paymentsMap).reduce((sum, val) => sum + Number(val), 0);
    },

    remainingAmount() {
      return (this.totalRequired - this.totalPaidSoFar).toFixed(2);
    },

    hasNextStep() {
      return this.currentStepIndex < this.stepsQueue.length - 1;
    },

    isValid() {
      return Math.abs(this.totalRequired - this.totalPaidSoFar) < 0.1;
    },
    defaultPaymentImg() {
      return (event) => event.target.src = require('@/assets/img/svg/default-img.svg')
    },
  },
  methods: {
    // --- Setup ---
    startPayment(methodsArray) {
      this.stepsQueue = methodsArray;
      this.currentStepIndex = 0;
      this.paymentsMap = {};
      this.autoFillAmount();
    },

    startSplitPayment() {
      const cashMethod = this.paymentMethods.find(m => m.id === 1) || { id: 1, name: this.$t('cash') };
      const networkMethod = this.paymentMethods.find(m => m.id === 2) || { id: 2, name: this.$t('network') };
      this.startPayment([cashMethod, networkMethod]);
      this.$set(this.paymentsMap, cashMethod.id, "");
    },

    // --- Logic ---
    updateAmount(value) {
      const stepId = this.currentStepInfo.id;
      this.$set(this.paymentsMap, stepId, value);
    },

    nextStep() {
      if (this.hasNextStep) {
        this.currentStepIndex++;
        this.autoFillAmount();
      }
    },

    prevStep() {
      if (this.currentStepIndex > 0) {
        this.currentStepIndex--;
      }
    },

    autoFillAmount() {
      const stepId = this.currentStepInfo.id;
      if (this.remainingAmount > 0) {
        this.$set(this.paymentsMap, stepId, String(this.remainingAmount));
      }
    },

    setCartType(id) {
      this.cart_type = id;
    },

    // --- Submit ---
    submitPayment() {
      const payload = this.stepsQueue.map(step => {

        let targetMachineId = 0;
        if (step.id === 2) {
          targetMachineId = this.machineID;
        }

        let paymentData = {
          machine_id: targetMachineId,
          amount: Number(this.paymentsMap[step.id]),
          payment_type: step.id,
          payment_text: step.name,
        };

        // if cart_type is 2, then cart_type is the cart_type
        if (step.id === 2) {
          paymentData.cart_type = this.cart_type;
        }

        return paymentData;
      });

      this.backValueMethod(payload);
      this.closeDialog();
    },

    resetPayment() {
      this.stepsQueue = [];
      this.paymentsMap = {};
      this.currentStepIndex = 0;
      this.cart_type = 3;
    },

    closeDialog() {
      this.localDialog = false;
      this.resetPayment();
    },

    // --- Handlers ---
    handleKeydown(e) {
      if (!this.localDialog) return;

      if (e.key === 'Enter' || e.code === 'NumpadEnter') {
        e.preventDefault();
        if (this.isPaymentActive) {
          if (this.hasNextStep) this.nextStep();
          else if (this.isValid) this.submitPayment();
        }
      }

      if (e.key === 'Escape') this.closeDialog();
    },

    handleGlobalShortcuts(e) {
      if (e.altKey && e.code === "KeyP") this.localDialog = true;
    }
  },

  mounted() {
    window.addEventListener("keydown", this.handleGlobalShortcuts);
    window.addEventListener("keydown", this.handleKeydown);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleGlobalShortcuts);
    window.removeEventListener("keydown", this.handleKeydown);
  }
};
</script>

<style scoped lang="scss"></style>